import React, { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  Form,
  Button,
  TextFieldControlled as Input,
  SelectFieldNativeControlled as Select,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useStatesList } from 'hooks/useStatesList';

const companyTypeList = [
  'Advocacy Group',
  'CBD Company',
  'Delivery Company',
  'Dispensary',
  'Individual',
  'Influencer',
  'Medical Community',
  'Publisher',
  'Other',
].map(companyType => {
  return {
    label: companyType,
    value: companyType,
  };
});

export default function PartnerForm({ formId }: { formId: string }) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const posthog = usePostHog();
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser } = useBrazeContext();
  const { translations } = useWordPressContext();
  const statesList = useStatesList();

  async function handleSubmit({
    name,
    email,
    phone,
    company,
    companyType,
    state,
    additionalComments,
    hearAboutUs,
  }) {
    // https://posthog.com/docs/product-analytics/identify#alias-assigning-multiple-distinct-ids-to-the-same-user
    posthog.alias(email);

    saveBrazeUser({
      name,
      email,
      phone,
      customAttributes: {
        state: translations[state.toUpperCase()],
        company,
        companyType,
      },
    });

    trackEvent({
      event: 'form_submit',
      form_name: `formType_${window?.location?.pathname}`,
      form_destination: window?.location?.pathname,
      form_submit_text: translations['Apply Now'],
      formType: 'partner_with_us',
      partnershipType: formId,
      company,
      companyType,
      additionalComments,
      hearAboutUs,
    });

    setFormSubmitted(true);
  }

  return (
    <Form
      className="gap-y-4"
      mode="onSubmit"
      defaultValues={{
        name: '',
        email: '',
        phone: '',
        company: '',
        companyType: '',
        state: '',
        additionalComments: '',
        hearAboutUs: '',
      }}
      onSubmit={handleSubmit}
    >
      {formSubmitted ? (
        <p>
          Thanks for getting in touch. A member of our outreach team will be in
          touch shortly.
        </p>
      ) : (
        <>
          <Input
            className="rounded-lg"
            type="text"
            name="name"
            placeholder={translations['Name']}
            validations={['required']}
          />
          <Input
            className="rounded-lg"
            type="email"
            name="email"
            placeholder={translations['Email']}
            validations={['required', 'email']}
          />
          <Input
            className="rounded-lg"
            type="tel"
            name="phone"
            placeholder="Phone (optional)"
          />
          <Input
            className="rounded-lg"
            type="text"
            name="company"
            placeholder="Company"
          />
          <Select
            aria-label="Select your state"
            name="state"
            placeholder={translations['State']}
            options={statesList}
            className="rounded-lg"
            validations={['required']}
          />
          <Select
            aria-label="Select your company type"
            name="companyType"
            placeholder="Select the most accurate category for your company"
            options={companyTypeList}
            className="rounded-lg"
            validations={['required']}
          />
          <Input
            className="rounded-lg"
            type="textarea"
            name="additionalComments"
            placeholder="What type of partnership are you are interested in?"
          />
          <Input
            className="rounded-lg"
            type="text"
            name="hearAboutUs"
            placeholder="How did you hear about us?"
          />
          <Button
            type="submit"
            label={translations['Apply Now']}
            name="submit"
            variant="secondary"
            className="w-full rounded-lg"
          />
        </>
      )}
    </Form>
  );
}
